import { useEffect, useState } from "react";
import { StatusEnum } from "../../../../common/data/base/enums/statusEnum";
import MESSAGES from "../../../../common/data/constants/messages";
import { IJobTitle } from "../../../../common/data/dtos/job-title";
import { IWorkResource } from "../../../../common/data/dtos/work-resource";
import { IWorkResourceJobTitle } from "../../../../common/data/dtos/work-resource-job-title";
import WorkResourceEnum from "../../../../common/data/enums/enumWorkResouce";
import Badge from "../../../../components/bootstrap/Badge";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import InputGroup from "../../../../components/bootstrap/forms/InputGroup";
import Select from "../../../../components/bootstrap/forms/Select";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import workResourceJobTitleService from "../../../../services/work-resource-job-title-service";
import workResourceService from "../../../../services/work-resource-service";

interface Props {
    isModalOpened: boolean;
    selectedJobTitle: IJobTitle | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const WorkResourceJobTitleModal = ({ isModalOpened, selectedJobTitle, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [workResources, setWorkResources] = useState<IWorkResource[]>([]);
    const [workResourcesJobTitle, setWorkResourcesJobTitle] = useState<IWorkResourceJobTitle[]>([]);
    const [selectedWorkResourceId, setSelectedWorkResourceId] = useState<string>('');

    useEffect(() => {

        if (selectedJobTitle) {
            fetchData();
            setSelectedWorkResourceId('');
        }

    }, [isModalOpened]);

    const fetchData = async () => {

        setIsSubmitting(true);

        try {

            if (!selectedJobTitle?.id) {
                showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION)
            } else {

                const [workResourcesResponse, workResourceJobTitleResponse] = await Promise.all([
                    workResourceService.getWorkResources(),
                    workResourceJobTitleService.getWorkResourceJobTitleByJobTitle(selectedJobTitle.id),
                ]);

                if (workResourcesResponse.error) {
                    showWarning(workResourcesResponse.error);
                    return;
                }

                if (workResourceJobTitleResponse.error) {
                    showWarning(workResourceJobTitleResponse.error);
                    return;
                }

                setWorkResources(workResourcesResponse.workResources || []);
                setWorkResourcesJobTitle(workResourceJobTitleResponse.workResourcesJobTitle || []);
            }


        } catch (error) {
            console.error('Erro nas requisições:', error);
            showWarning('Erro ao carregar dados');
        } finally {
            setIsSubmitting(false);
        }

    }

    const fetchWorkResourceJobTitle = async () => {

        if (selectedJobTitle?.id) {

            const response = await workResourceJobTitleService.getWorkResourceJobTitleByJobTitle(selectedJobTitle?.id)

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setWorkResourcesJobTitle(response.workResourcesJobTitle || []);
        }
    }

    const onFormSubmit = async () => {

        if (!workResourcesJobTitle) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION)
            return;
        }

        const itensToAdd = workResourcesJobTitle
            .filter(item => !item.id || item.id.trim() === '')
            .map(({ workResource, ...toAdd }) => toAdd);


        if (!itensToAdd || itensToAdd.length === 0) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION_TO_ADD)
            return;
        }

        setIsSubmitting(true);

        const response = await workResourceJobTitleService.createWorkResourcesJobTitle(itensToAdd);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        setSelectedWorkResourceId('');
        onAfterSubmit();
    }

    const onClose = () => {
        onModalClose();
    };

    const handleAddClick = async () => {

        if (!selectedJobTitle?.id || !selectedWorkResourceId) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            return;
        }

        const exists = workResourcesJobTitle.some(item => item.workResourceId === selectedWorkResourceId);
        if (exists) {
            showWarning(MESSAGES.VALIDATION.ITEM_EXISTS);
            return;
        } else {

            const selectedWorkResource = workResources.find(p => p.id === selectedWorkResourceId);

            const newWorkResourceJobTitle: IWorkResourceJobTitle = {
                jobTitleId: selectedJobTitle?.id,
                workResourceId: selectedWorkResourceId,
                rowVersion: 1,
                status: StatusEnum.Active,
                workResource: selectedWorkResource
            };

            setWorkResourcesJobTitle((prev) => [
                ...prev,
                newWorkResourceJobTitle,
            ]);
        }
    }

    const handleDeleteClick = async (workResourceJobTitle: IWorkResourceJobTitle) => {

        if (!workResourceJobTitle.id) {
            const filteredList = workResourcesJobTitle.filter(item => item.workResourceId !== workResourceJobTitle.workResourceId);
            setWorkResourcesJobTitle(filteredList);
        } else {

            setIsSubmitting(true);

            const response = await workResourceJobTitleService.deleteWorkResourceJobTitle(workResourceJobTitle.id);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchWorkResourceJobTitle();

        }
    }

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'xl'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='work-resource-job-title-modal'>Manutenção de ferramentas e EPI da função</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title={selectedJobTitle?.name || 'Manutenção de ferramentas e EPI da função'} icon='Work'>
                        <div className='row g-4'>
                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <InputGroup
                                    isWrap={true}
                                >
                                    <FormGroup
                                        id='workResource'
                                        label='Ferramentas e EPI'
                                        isFloating
                                    >
                                        <Select
                                            ariaLabel='Escolha uma ferramentas ou EPI'
                                            list={[
                                                { value: '', text: 'Selecione uma Ferramenta ou EPI' },
                                                ...workResources.map((workResource) => ({
                                                    value: workResource.id,
                                                    text: `${workResource.description} [${workResource.approvalCertificate}]`
                                                }))
                                            ]}
                                            onChange={(e: { target: { value: any } }) => {
                                                if (e.target.value) {
                                                    setSelectedWorkResourceId(e.target.value);
                                                }
                                            }}
                                        />
                                    </FormGroup>

                                    <Button
                                        icon='AddCircleOutline'
                                        color='primary'
                                        onClick={handleAddClick}
                                    >
                                        Adicionar
                                    </Button>
                                </InputGroup>
                            </div>

                            <div className='col-lg-12 col-md-12 col-sm-12 table-responsive '>

                                <table className='table table-modern table-hover'>
                                    <thead>
                                        <tr>
                                            <th>Descrição</th>
                                            <th>CA</th>
                                            <th>Tipo</th>
                                            <td aria-labelledby='Actions' />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {workResourcesJobTitle.map((item: IWorkResourceJobTitle) => (
                                            <tr key={item.id}>
                                                <td>{item.workResource?.description}</td>
                                                <td>{item.workResource?.approvalCertificate || '-'}</td>
                                                <td>
                                                    {item.workResource?.type === WorkResourceEnum.tool ? (
                                                        <h5>
                                                            <Badge color='info' className='align-items-center justify-content-center'>
                                                                Ferramenta
                                                            </Badge>
                                                        </h5>
                                                    ) : (
                                                        <h5>
                                                            <Badge color='info' className='align-items-center justify-content-center'>
                                                                EPI
                                                            </Badge>
                                                        </h5>
                                                    )}
                                                </td>


                                                <td className='text-end'>
                                                    <Button
                                                        color='dark'
                                                        isLight
                                                        icon='Delete'
                                                        onClick={() => handleDeleteClick(item)}
                                                        aria-label='Remover'
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={onFormSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};